import React, { useState, useEffect } from 'react';
import { Bed, Wifi, Car, Coffee, Users, ArrowRight, ChevronLeft, ChevronRight, MapPin, ExternalLink } from 'lucide-react';
import { getEndpointUrl } from '../config/api';
import WhatsAppFloat from './WhatsAppFloat';
import './Home.css';

const Home = ({ onBookRoomsClick }) => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
    // Hero images for the slider
  const heroImages = [
    '/images/hero/hero1.jpg',
    '/images/hero/hero2.jpg', 
    '/images/hero/hero3.jpg',
    '/images/hero/hero4.jpg',
    '/images/hero/hero5.jpg',
    '/images/hero/hero6.jpg'
  ];
  // Auto-slide functionality
  useEffect(() => {
    if (!isPaused) {
      const slideInterval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % heroImages.length);
      }, 4000); // Change slide every 4 seconds (faster with more images)

      return () => clearInterval(slideInterval);
    }
  }, [heroImages.length, isPaused]);

  // Preload hero images for better performance
  useEffect(() => {
    heroImages.forEach(imageSrc => {
      const img = new Image();
      img.src = imageSrc;
    });
  }, []);

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + heroImages.length) % heroImages.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };
  // Fetch rooms from backend
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        // Use ALL rooms endpoint instead of available only, since we have date range booking
        const response = await fetch(getEndpointUrl('ROOMS', 'ALL'));        if (response.ok) {
          const roomsData = await response.json();
          // Take first 6 rooms for home page display to show more options
          setRoomTypes(roomsData.slice(0, 6));
        } else {
          console.error('Failed to fetch rooms from backend');
          setRoomTypes([]); // No fallback - show empty state
        }
      } catch (error) {
        console.error('Error fetching rooms:', error);
        setRoomTypes([]); // No fallback - show empty state
      }
    };

    fetchRooms();
  }, []);  // Helper function to get room description with enhanced defaults
  const cleanRoomDescription = (description, roomName, roomSize) => {
    if (description && description.trim() && description.length >= 10) {
      return description.trim();
    }
    
    // Enhanced default descriptions based on room name/type
    const roomType = roomName?.toLowerCase() || '';
    
    if (roomType.includes('delux') || roomType.includes('deluxe')) {
      return `Experience comfort in our spacious ${roomSize || 'well-appointed'} deluxe container room. Features premium bedding, modern AC, attached bathroom with 24/7 hot water, flat-screen TV, and thoughtfully designed interiors that blend industrial charm with contemporary comfort.`;
    } else if (roomType.includes('premier') || roomType.includes('premium')) {
      return `Indulge in luxury with our ${roomSize || 'elegantly designed'} premier container room. Boasts sophisticated thematic interiors, superior amenities, individual climate control, modern bathroom facilities, entertainment system, and panoramic views of Kolhapur's landscape.`;
    } else if (roomType.includes('super') || roomType.includes('suite')) {
      return `Ultimate luxury awaits in our ${roomSize || 'expansive'} super deluxe container suite. Features separate living area, premium furnishings, advanced amenities, spa-like bathroom with premium fixtures, work desk, mini-bar area, and exclusive container elevation design.`;
    } else if (roomType.includes('standard') || roomType.includes('classic')) {
      return `Comfortable and affordable ${roomSize || 'cozy'} container room perfect for budget-conscious travelers. Includes essential amenities like AC, clean attached bathroom, hot water facility, cable TV, and our signature container hospitality experience.`;
    } else {
      return `Uniquely designed ${roomSize || 'comfortable'} container room offering modern amenities and authentic Kolhapuri hospitality. Features air conditioning, attached bathroom with 24/7 hot water, flat-screen TV, and sustainable container architecture that creates a memorable stay experience.`;
    }
  };
  const facilities = [
    { icon: Wifi, name: 'Free WiFi', description: 'High-speed internet throughout the hotel' },
    { icon: Car, name: 'Free Parking', description: 'Complimentary parking facility for all guests' },
    { icon: Coffee, name: '24/7 Room Service', description: 'Prompt room service with authentic Kolhapuri cuisine' },
    { icon: Users, name: 'Banquet Hall', description: 'Modern banquet hall perfect for events (70-80 pax)' },
    { icon: Bed, name: 'AC Rooms', description: 'Stylish container elevation with thematic interiors' },
    { icon: Coffee, name: 'Restaurant', description: 'Authentic Kolhapuri veg and non-veg thali experience' }
  ];
    const nearbyAttractions = [
    {
      name: 'Mahalaxmi Temple',
      distance: '3.2 km',
      description: 'Ancient temple dedicated to Goddess Mahalaxmi, one of the most revered temples in Kolhapur',
      type: 'Religious Site',
      image: '/images/attractions/mahalaxmi-temple.jpg'
    },
    {
      name: 'Panhala Fort',
      distance: '18 km',
      description: 'Historic hill fortress offering panoramic views and rich Maratha history',
      type: 'Historical Fort',
      image: '/images/attractions/panhala-fort.jpg'
    },
    {
      name: 'Rankala Lake',
      distance: '4.5 km',
      description: 'Scenic artificial lake perfect for evening walks and boating activities',
      type: 'Natural Beauty',
      image: '/images/attractions/rankala-lake.jpg'
    },
    {
      name: 'New Palace Museum',
      distance: '2.8 km',
      description: 'Former royal palace showcasing Kolhapur\'s royal heritage and artifacts',
      type: 'Museum',
      image: '/images/attractions/new-palace.jpg'
    },    {
      name: 'Narsoba Wadi',
      distance: '12 km',
      description: 'Sacred religious site and pilgrimage destination known for its spiritual significance and peaceful environment',
      type: 'Religious Site',
      image: '/images/attractions/narsinhwadi.jpeg'
    },
    {
      name: 'Khidrapur',
      distance: '25 km',
      description: 'Historic town famous for its ancient Ganesha temple and traditional Marathi culture',
      type: 'Religious & Cultural Site',
      image: '/images/attractions/Khidrapur-1.jpg'
    },
    {
      name: 'Dajipur Wildlife Sanctuary',
      distance: '65 km',
      description: 'Wildlife sanctuary famous for Indian bison and diverse flora and fauna',
      type: 'Wildlife Sanctuary',
      image: '/images/attractions/dajipur-sanctuary.jpg'
    }
  ];
    return (
    <div className="home">
      {/* WhatsApp Float */}
      <WhatsAppFloat />
      
      {/* Hero Section with Image Slider */}
      <section 
        className="hero"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="hero-slider">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{ backgroundImage: `url(${image})` }}
            >
              <div className="hero-overlay"></div>
            </div>
          ))}
        </div>
        
        {/* Navigation Arrows */}
        <button className="hero-nav prev" onClick={prevSlide}>
          <ChevronLeft size={24} />
        </button>
        <button className="hero-nav next" onClick={nextSlide}>
          <ChevronRight size={24} />
        </button>
        
        {/* Slide Indicators */}
        <div className="hero-indicators">
          {heroImages.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
          <div className="hero-content">
          <div className="hero-logo">
            <img src="/images/logo/container-logo.png" alt="Hotel The Container" className="hero-logo-image" />
          </div>
          <p>A Unique Memorable Stay with Authentic Taste of Kolhapur</p>
          <p className="hero-subtitle">Where industrial charm meets modern comfort in the heart of Kolhapur</p>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <button 
              onClick={onBookRoomsClick} 
              className="cta-button"
              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', cursor: 'pointer', color: 'white', padding: '12px 24px', borderRadius: '6px', fontSize: 'inherit', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              Book Your Stay Now <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>{/* Room Types Section */}
      <section className="room-types">
        <div className="container">
          <h2>Our Stylish Container Rooms</h2>
          <p className="section-subtitle">Thoughtfully designed for a cozy and memorable stay</p>
          {roomTypes.length > 0 ? (            <div className="room-grid">
              {roomTypes.map(room => (
              <div key={room.id} className="room-card-wrapper">
                <div className="room-card">
                  <div className="room-image">                  <img 
                    src={
                      (room.imageUrl && !room.imageUrl.includes('/api/placeholder/')) ? room.imageUrl :
                      (room.image && !room.image.includes('/api/placeholder/')) ? room.image :
                      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjI1MCIgdmlld0JveD0iMCAwIDQwMCAyNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMjUwIiBmaWxsPSIjRTJFOEYwIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTI1IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM2NDc0OEIiPlJvb20gSW1hZ2U8L3RleHQ+Cjwvc3ZnPgo='                    } 
                    alt={room.name}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjI1MCIgdmlld0JveD0iMCAwIDQwMCAyNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMjUwIiBmaWxsPSIjRTJFOEYwIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTI1IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM2NDc0OEIiPlJvb20gSW1hZ2U8L3RleHQ+Cjwvc3ZnPgo=';
                    }}
                  />
                  <div className="room-price-info">
                    <div className="price-from">From</div>
                    <div className="price-amount">₹{room.singleOccupancyPrice || room.price}</div>
                    <div className="price-period">/night*</div>
                    <div className="price-disclaimer">*Prices may vary by date</div>
                    <div className="breakfast-badge">Complimentary Breakfast Included</div>
                  </div>
                </div>                <div className="room-info">
                  <h3>{room.name}</h3>
                  <p className="room-size">Room Size: {room.size}</p>
                  <p className="room-description">{cleanRoomDescription(room.description, room.name, room.size)}</p><div className="room-features">
                    {(room.facilities || room.features || []).map((feature, index) => (
                      <span key={index} className="feature-tag">{feature}</span>                    ))}
                  </div>
                  <div className="room-pricing-summary">
                    <div className="pricing-options">
                      <span className="single-rate">Single: ₹{room.singleOccupancyPrice || room.price}</span>
                      <span className="double-rate">Double: ₹{room.doubleOccupancyPrice || room.price}</span>
                    </div>
                    <div className="pricing-note">
                      <small>* Base rates shown. Holiday & festive pricing may apply.</small>
                    </div>
                    {(room.extraBedPrice > 0 || room.breakfastPrice > 0) && (
                      <div className="additional-services">
                        {room.extraBedPrice > 0 && <span className="extra-bed">Extra bed: +₹{room.extraBedPrice}</span>}
                        {room.breakfastPrice > 0 && <span className="breakfast">Complimentary breakfast included</span>}
                      </div>
                    )}
                  </div>
                  <div className="book-action">
                    <button 
                      onClick={onBookRoomsClick}
                      className="book-button"
                      style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', cursor: 'pointer', color: 'white', padding: '8px 16px', borderRadius: '4px', fontSize: 'inherit', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      Book Now <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            ))}
          </div>
          ) : (
            <div className="no-rooms-message">
              <p>No rooms available at the moment. Please check back later or contact us directly.</p>
            </div>
          )}
        </div>
      </section>

      {/* Facilities Section */}
      <section className="facilities">
        <div className="container">
          <h2>Hotel Facilities</h2>
          <div className="facilities-grid">
            {facilities.map((facility, index) => (
              <div key={index} className="facility-card">
                <facility.icon size={40} className="facility-icon" />
                <h3>{facility.name}</h3>
                <p>{facility.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>      {/* Nearby Attractions Section */}
      <section className="nearby-attractions">
        <div className="container">
          <div className="section-header">
            <h2>Explore Kolhapur</h2>
            <p className="section-subtitle">Discover the rich heritage and natural beauty around our hotel</p>
          </div>
          
          <div className="attractions-grid">
            {nearbyAttractions.map((attraction, index) => (
              <div key={index} className="attraction-card">
                <div className="attraction-image">
                  <img 
                    src={attraction.image} 
                    alt={attraction.name}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjI1MCIgdmlld0JveD0iMCAwIDQwMCAyNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMjUwIiBmaWxsPSIjRTJFOEYwIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTI1IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM2NDc0OEIiPkF0dHJhY3Rpb248L3RleHQ+Cjwvc3ZnPgo=';
                    }}
                  />
                  <div className="attraction-type">{attraction.type}</div>
                  <div className="attraction-distance">
                    <MapPin size={16} />
                    {attraction.distance}
                  </div>
                </div>
                
                <div className="attraction-info">
                  <h3>{attraction.name}</h3>
                  <p>{attraction.description}</p>
                  <div className="attraction-action">
                    <span className="distance-highlight">Just {attraction.distance} away</span>
                  </div>
                </div>
              </div>
            ))}
          </div>            <div className="location-highlight">
            <div className="location-content">
              <h3>🗺️ Prime Location in Kolhapur - Gateway to Mahalaxmi Temple</h3>
              <p>
                Hotel The Container is strategically positioned just <strong>3.2 km from the sacred Mahalaxmi Temple</strong>, 
                one of Maharashtra's most revered Shakti Peethas. Our prime location makes us the ideal accommodation 
                for pilgrims and devotees visiting this ancient temple dedicated to Goddess Mahalaxmi.
              </p>
              
              <div className="temple-benefits">
                <h4>🕉️ Why Our Location is Perfect for Temple Visitors:</h4>
                <ul className="benefits-list">
                  <li><strong>Early Morning Darshan:</strong> Just 5-minute drive for peaceful morning prayers before crowds arrive</li>
                  <li><strong>Multiple Visits:</strong> Convenient for attending different aartis throughout the day</li>
                  <li><strong>Rest Between Visits:</strong> Return to comfortable AC rooms between temple visits during festivals</li>
                  <li><strong>Local Guidance:</strong> Our staff provides temple timings, special puja information, and directions</li>
                  <li><strong>Authentic Prasad:</strong> Store temple prasad safely in our room refrigerators</li>
                  <li><strong>Festival Convenience:</strong> Prime location during Navratri and other major celebrations</li>
                </ul>
              </div>
              
              <div className="spiritual-experience">
                <p>
                  <em>"Experience the divine energy of Kolhapur's spiritual heart while enjoying modern comfort. 
                  Whether you're here for a quick darshan or an extended spiritual retreat, our location puts 
                  you at the center of Kolhapur's rich religious heritage."</em>
                </p>
              </div>
              
              <div className="location-actions">
                <a 
                  href="https://maps.app.goo.gl/UbarUB5SMs8bFLeE8" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="location-button temple-button"
                >
                  <MapPin size={20} />
                  Route to Mahalaxmi Temple
                  <ExternalLink size={16} />
                </a>
                <a 
                  href="https://maps.app.goo.gl/UbarUB5SMs8bFLeE8" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="location-button"
                >
                  <MapPin size={20} />
                  View Hotel Location
                  <ExternalLink size={16} />
                </a>
              </div>
              
              <div className="address-info">
                <span className="address-label">Hotel Address:</span>
                <span className="address-text">Hotel The Container, Kolhapur, Maharashtra</span>
                <span className="distance-highlight">📍 Only 3.2 km from Mahalaxmi Temple</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about">
        <div className="container">
                   
          <div className="about-content">
            <div className="about-text">
              <h2>Hotel the Container – Not Just a Stay, But a Story</h2>
              <p>
                Welcome to Hotel the Container, where industrial charm meets modern comfort. 
                Designed using upcycled shipping containers, our hotel offers a refreshing blend 
                of sustainability, creativity, and hospitality — right in the heart of Kolhapur.
              </p>
              <p>
                Our rooms are thoughtfully designed with thematic interiors and unique container 
                elevation, featuring comfortable bedding, smart lighting, modern amenities, 
                attached clean bathrooms with 24x7 hot water, and flat-screen TV.
              </p>
              
              <div className="cuisine-highlight">
                <h3>🍴 Authentic Kolhapuri Flavors</h3>
                <p>
                  Indulge in bold local tastes at our in-house AC restaurant featuring authentic 
                  Kolhapuri veg and non-veg thali – a must-try for food lovers. Includes spicy 
                  chicken/mutton/anda/veg thali, tambda rassa, pandhra rassa, solkadi, bhakri/chapati/roti, 
                  rice, and accompaniments made with age-old recipes and fresh spices.
                </p>
              </div>
              
              <div className="hotel-location">
                <h3>📍 Find Us in Kolhapur</h3>
                <p>Conveniently located in the heart of the city with easy access to major attractions.</p>
                <a 
                  href="https://maps.app.goo.gl/UbarUB5SMs8bFLeE8" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="maps-button"
                >
                  <MapPin size={18} />
                  Get Directions
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>
          <div className="tagline">
            <p><em>Experience Kolhapur. Differently. Create memories with us.</em></p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
